import { useState } from 'react';
import { useWatch, type Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@monksflow/monks-ui';
import { FileDropzone } from '@/shared/ui/FileDropzone';
import type { SupportRequestFormValues } from '../../model/support-request.schema';
import {
  BLOCKER_ANSWERS,
  FEATURE_FREQUENCIES,
  PLATFORM_AREAS,
  SUPPORT_REQUEST_TYPES,
} from '../../model/support-request.types';

const ATTACHMENT_ACCEPT = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'video/mp4': ['.mp4'],
};
const ATTACHMENT_MAX_SIZE = 25 * 1024 * 1024;

function RequiredMark() {
  return (
    <span aria-hidden="true" className="text-destructive">
      *
    </span>
  );
}

interface SupportRequestFieldsProps {
  control: Control<SupportRequestFormValues>;
  disabled: boolean;
}

export function SupportRequestFields({ control, disabled }: SupportRequestFieldsProps) {
  const { t } = useTranslation('common');
  const type = useWatch({ control, name: 'type' });
  const [hasRejectedFiles, setHasRejectedFiles] = useState(false);

  return (
    <>
      <FormField
        control={control}
        name="type"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('supportRequest.supportRequestFields.howCanWeHelp')}</FormLabel>
            <Select value={field.value} onValueChange={field.onChange} disabled={disabled}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {SUPPORT_REQUEST_TYPES.map((option) => (
                  <SelectItem key={option} value={option}>
                    {t(`supportRequest.supportRequestFields.type.${option}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="area"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t('supportRequest.supportRequestFields.areaOfPlatform')}
              <RequiredMark />
            </FormLabel>
            <Select value={field.value} onValueChange={field.onChange} disabled={disabled}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue
                    placeholder={t('supportRequest.supportRequestFields.selectAnArea')}
                  />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {PLATFORM_AREAS.map((area) => (
                  <SelectItem key={area} value={area}>
                    {t(`supportRequest.supportRequestFields.area.${area}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      {type === 'bug' && (
        <>
          <FormField
            control={control}
            name="currentBehavior"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t('supportRequest.supportRequestFields.currentBehavior')}
                  <RequiredMark />
                </FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    className="min-h-20"
                    placeholder={t('supportRequest.supportRequestFields.whatIsHappeningRightNow')}
                    disabled={disabled}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="expectedBehavior"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('supportRequest.supportRequestFields.expectedBehavior')}</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    className="min-h-20"
                    placeholder={t(
                      'supportRequest.supportRequestFields.whatDidYouExpectToHappenInstead',
                    )}
                    disabled={disabled}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </>
      )}

      {type === 'feature' && (
        <FormField
          control={control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('supportRequest.supportRequestFields.descriptionOfFeature')}
                <RequiredMark />
              </FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  className="min-h-30"
                  placeholder={t(
                    'supportRequest.supportRequestFields.describeTheFeatureYouHaveInMind',
                  )}
                  disabled={disabled}
                />
              </FormControl>
              <FormDescription>
                {t('supportRequest.supportRequestFields.whatShouldItDo')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {type === 'question' && (
        <FormField
          control={control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('supportRequest.supportRequestFields.whatWouldYouLikeToLearnMoreAbout')}
                <RequiredMark />
              </FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  className="min-h-30"
                  placeholder={t('supportRequest.supportRequestFields.askYourQuestion')}
                  disabled={disabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {type === 'feature' && (
        <FormField
          control={control}
          name="frequency"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('supportRequest.supportRequestFields.howOftenDoesThisComeUp')}
                <RequiredMark />
              </FormLabel>
              <Select value={field.value ?? ''} onValueChange={field.onChange} disabled={disabled}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t('supportRequest.supportRequestFields.selectAFrequency')}
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {FEATURE_FREQUENCIES.map((frequency) => (
                    <SelectItem key={frequency} value={frequency}>
                      {t(`supportRequest.supportRequestFields.frequency.${frequency}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <FormField
        control={control}
        name="attachments"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('supportRequest.supportRequestFields.attachments')}</FormLabel>
            <FileDropzone
              accept={ATTACHMENT_ACCEPT}
              maxSize={ATTACHMENT_MAX_SIZE}
              disabled={disabled}
              title={t('supportRequest.supportRequestFields.dragAScreenshotHereOrBrowse')}
              caption={t('supportRequest.supportRequestFields.attachmentFormatsHint')}
              onDrop={(accepted, rejections) => {
                setHasRejectedFiles(rejections.length > 0);
                field.onChange([...field.value, ...accepted]);
              }}
            />
            {field.value.length > 0 && (
              <div className="flex flex-wrap gap-xs">
                {field.value.map((file, index) => (
                  <Badge
                    key={`${file.name}-${index}`}
                    variant="outline"
                    disabled={disabled}
                    onRemove={() => field.onChange(field.value.filter((_, i) => i !== index))}
                  >
                    {file.name}
                  </Badge>
                ))}
              </div>
            )}
            <FormMessage>
              {hasRejectedFiles && t('supportRequest.supportRequestFields.attachmentsSkippedHint')}
            </FormMessage>
            <FormDescription>
              {t('supportRequest.supportRequestFields.screenshotsHelpUsReproduce')}
            </FormDescription>
          </FormItem>
        )}
      />

      {type === 'bug' && (
        <FormField
          control={control}
          name="isBlocker"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('supportRequest.supportRequestFields.isThisABlockerForYou')}
                <RequiredMark />
              </FormLabel>
              <FormControl>
                <RadioGroup
                  className="flex gap-lg"
                  value={field.value ?? ''}
                  onValueChange={field.onChange}
                  disabled={disabled}
                >
                  {BLOCKER_ANSWERS.map((answer) => (
                    <div key={answer} className="flex items-center gap-xs">
                      <RadioGroupItem value={answer} id={`support-request-blocker-${answer}`} />
                      <Label htmlFor={`support-request-blocker-${answer}`}>
                        {t(`supportRequest.supportRequestFields.${answer}`)}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </>
  );
}
